<script setup>
import { computed, onMounted, ref } from 'vue'

import { useRouter, useRoute } from 'vue-router'
const router = useRouter()
const route = useRoute()

import { useAuth } from '@store/auth'
const $auth = useAuth()

import { useAppointments } from '@store/appointments.js'
const $appointments = useAppointments()

import { useSettings } from '@store/settings.js'
const $settings = useSettings()

import { useCustomizer } from '@store/customizer.js'
const $customizer = useCustomizer()

import { Tab, Tabs, Input, Label, Select, Button } from '@components'


// import AccountLoader from './AccountLoader.vue'
import Main from '../layout/Main.vue'

onMounted(() => {
    if (!router.currentRoute.value.query.tab) {
        router.push({ query: { tab: 'register' } }) // button show on #footer depending on router query
    }

})

$appointments.matchAppointmentPreRequirement()

const validationCheck = (conditions) => {
    $auth.state.errors = {}
    let errors = 0
    conditions.forEach((item) => {
        if (item.condition) {
            errors++
            $auth.state.errors[item.field] = item.message
        }
    })
    return {
        errors: errors > 0 ? true : false
    }
}

import { regex } from '@utils'

const loginFormRef = ref(null)
const registerFormRef = ref(null)


// Methods
const submitRegisterForm = async () => {
    const { errors } = validationCheck([
        {
            field: 'first_name',
            condition: !$auth.registerForm.first_name,
            message: 'First name is required'
        },
        {
            field: 'last_name',
            condition: !$auth.registerForm.last_name,
            message: 'Last name is required'
        },
        {
            field: 'email',
            condition: !$auth.registerForm.email,
            message: 'Email is required'
        },
        {
            field: 'email',
            condition: !regex.email.test($auth.registerForm.email),
            message: 'Invalid email format'
        },
        {
            field: 'phone',
            condition: !$auth.registerForm.phone,
            message: 'Phone is required'
        },
        {
            field: 'phone',
            condition: !regex.phone.test($auth.registerForm.phone),
            message: 'Invalid phone format'
        },
        {
            field: 'password',
            condition: !$auth.registerForm.password,
            message: 'Password is required'
        }
    ])
    if (errors) return

    if (!registerFormRef.value.checkValidity()) return registerFormRef.value.reportValidity()

    const user = await $auth.submitRegisterForm()
    if (user.customer_id) {
        window.prebook.customer.customer_id = user.customer_id
        $appointments.nextStep()
    }
    Log('Registered', user)
}
const submitLoginForm = async () => {
    const { errors } = validationCheck([
        {
            field: 'email',
            condition: !$auth.loginForm.email,
            message: 'Email is required'
        },
        {
            field: 'email',
            condition: !regex.email.test($auth.loginForm.email),
            message: 'Invalid email format'
        },
        {
            field: 'password',
            condition: !$auth.loginForm.password,
            message: 'Password is required'
        }
    ])
    if (errors) return

    if (!loginFormRef.value.checkValidity()) return loginFormRef.value.reportValidity()

    const user = await $auth.submitLoginForm()
    if (user.customer_id) {
        window.prebook.customer.customer_id = user.customer_id
        // $appointments.nextStep()
        router.push({ name: 'overview' })
    }

    Log('Logged in', user)
}

router.beforeEach((to, from, next) => {
    $auth.state.errors = {}
    next()
})

const showPlaceholder = computed(() => $settings.customizer.customer_information.show_placeholder || false)

const activeTab = ref('')
</script>


<style lang="scss" scoped></style>

<template>
    <Main slotContentClass="h-[calc(100%-67px)]" height="550px">
        <template #content>
            <div class="account-page-title" :style="$customizer.titleStyle">{{ $settings.customizer.customer_information.title || '' }}</div>
            <div class="account-page-subtitle">{{ $settings.customizer.customer_information.subtitle || '' }}</div>

            <!-- empty Account loader -->
            <!-- <AccountLoader/> -->

            <!-- Account list -->
            <Tabs default="register" :tabWrapperClass="`mb-[20px] ${!$customizer.isRounded ? 'rounded-none' : ''}`" :tabClass="{ 'rounded-none': !$customizer.isRounded }" @update:tab="(value) => activeTab = value">
                <Tab id="register">{{ $settings.customizer.customer_information.signup_title }}</Tab>
                <Tab id="login">{{ $settings.customizer.customer_information.login_title }}</Tab>

                <!-- create account tab -->
                <template #register>
                    <form ref="registerFormRef" @submit.prevent="submitRegisterForm">
                        <div class="grid grid-cols-2 gap-3">
                            <div class="w-full">
                                <Label>{{ $settings.customizer.customer_information.signup_firstname_label }} <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input :rounded="$customizer.isRounded" :borderStyle="$settings.customizer.design.input_border_style" :oultineColor="$settings.customizer.design.brand_color" :borderColor="$settings.customizer.design.input_border_color" :placeholder="showPlaceholder ? $settings.customizer.customer_information.signup_firstname_placeholder : ''" required v-model="$auth.registerForm.first_name" type="text" size="sm" class="mt-1.5" inputClass="text-sm"></Input>
                                <div v-if="$auth.state.errors.first_name" class="text-[#D92D20] text-sm  mt-1 ml-1">{{ $auth.state.errors.first_name }}</div>
                            </div>
                            <div class="w-full">
                                <Label>{{ $settings.customizer.customer_information.signup_lastname_label }} <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input :rounded="$customizer.isRounded" :borderStyle="$settings.customizer.design.input_border_style" :oultineColor="$settings.customizer.design.brand_color" :borderColor="$settings.customizer.design.input_border_color" :placeholder="showPlaceholder ? $settings.customizer.customer_information.signup_lastname_placeholder : ''" required v-model="$auth.registerForm.last_name" type="text" size="sm" class="mt-1.5" inputClass="text-sm"></Input>
                                <div v-if="$auth.state.errors.last_name" class="text-[#D92D20] text-sm  mt-1 ml-1">{{ $auth.state.errors.last_name }}</div>
                            </div>
                            <div class="col-span-2">
                                <Label>{{ $settings.customizer.customer_information.signup_email_label }} <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input :rounded="$customizer.isRounded" :borderStyle="$settings.customizer.design.input_border_style" :oultineColor="$settings.customizer.design.brand_color" :borderColor="$settings.customizer.design.input_border_color" :placeholder="showPlaceholder ? $settings.customizer.customer_information.signup_email_placeholder : ''" required v-model="$auth.registerForm.email" type="email" size="sm" class="mt-1.5" inputClass="text-sm"></Input>
                                <div v-if="$auth.state.errors.email" class="text-[#D92D20] text-sm  mt-1 ml-1">{{ $auth.state.errors.email }}</div>
                            </div>
                            <!-- phone -->
                            <div class="col-span-2">
                                <Label>{{ $settings.customizer.customer_information.signup_phonenumber_label }} <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input :rounded="$customizer.isRounded" :borderStyle="$settings.customizer.design.input_border_style" :oultineColor="$settings.customizer.design.brand_color" :borderColor="$settings.customizer.design.input_border_color" :placeholder="showPlaceholder ? $settings.customizer.customer_information.signup_phonenumber_placeholder : ''" required v-model="$auth.registerForm.phone" type="text" size="sm" class="mt-1.5" inputClass="text-sm"></Input>
                                <div v-if="$auth.state.errors.phone" class="text-[#D92D20] text-sm  mt-1 ml-1">{{ $auth.state.errors.phone }}</div>
                            </div>
                            <div class="col-span-2">
                                <Label>{{ $settings.customizer.customer_information.signup_password_label }} <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input :rounded="$customizer.isRounded" :borderStyle="$settings.customizer.design.input_border_style" :oultineColor="$settings.customizer.design.brand_color" :borderColor="$settings.customizer.design.input_border_color" :placeholder="showPlaceholder ? $settings.customizer.customer_information.signup_password_placeholder : ''" required v-model="$auth.registerForm.password" type="password" size="sm" class="mt-1.5" inputClass="text-sm"></Input>
                                <div v-if="$auth.state.errors.password" class="text-[#D92D20] text-sm  mt-1 ml-1">{{ $auth.state.errors.password }}</div>
                            </div>
                        </div>
                    </form>

                    <!-- <div class="mt-2">
                            <Button submit type="primary" size="md" radius="square" class="w-full" v-if="$router.currentRoute.value.query.tab == 'register'">{{ $auth.state.loading ? 'Loading...' : 'Signup' }}</Button>
                        </div> -->
                </template>

                <!-- login tab -->
                <template #login>
                    <form ref="loginFormRef" @submit.prevent="submitLoginForm">
                        <div class="flex flex-col gap-3">
                            <div class="col-span-2">
                                <Label>{{ $settings.customizer.customer_information.login_email_label }} <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input :rounded="$customizer.isRounded" :borderStyle="$settings.customizer.design.input_border_style" :oultineColor="$settings.customizer.design.brand_color" :borderColor="$settings.customizer.design.input_border_color" :placeholder="showPlaceholder ? $settings.customizer.customer_information.login_email_placeholder : ''" required v-model="$auth.loginForm.email" type="email" size="sm" class="mt-1.5" inputClass="text-sm"></Input>
                                <div v-if="$auth.state.errors.email" class="text-[#D92D20] text-sm  mt-1 ml-1">{{ $auth.state.errors.email }}</div>
                            </div>

                            <div class="col-span-2">
                                <Label>{{ $settings.customizer.customer_information.login_password_label }} <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input :rounded="$customizer.isRounded" :borderStyle="$settings.customizer.design.input_border_style" :oultineColor="$settings.customizer.design.brand_color" :borderColor="$settings.customizer.design.input_border_color" :placeholder="showPlaceholder ? $settings.customizer.customer_information.login_password_placeholder : ''" type="password" required v-model="$auth.loginForm.password" size="sm" class="mt-1.5" inputClass="text-sm"></Input>
                                <div v-if="$auth.state.errors.password" class="text-[#D92D20] text-sm  mt-1 ml-1">{{ $auth.state.errors.password }}</div>
                            </div>
                            <!-- <div class="text-end">
                                <div class="text-[#6941C6] text-base font-semibold cursor-pointer">Forgot password?</div>
                            </div> -->
                        </div>
                    </form>
                </template>
            </Tabs>

            <div class="text-[#bd4e4e] text-base font-medium mt-1 absolute" v-if="$auth.state.errors.message" v-html="$auth.state.errors.message"></div>
        </template>

        <!-- footer button -->
        <template #footer>
            <Button @click="submitRegisterForm" type="primary" size="md" radius="square" class="w-full" :buttonStyle="$customizer.buttonStyle" v-if="activeTab == 'register'">{{ $auth.state.loading ? 'Loading...' : $settings.customizer.customer_information.signup_button }}</Button>
            <Button @click="submitLoginForm" type="primary" size="md" radius="square" class="w-full" :buttonStyle="$customizer.buttonStyle" v-if="activeTab == 'login'">{{ $auth.state.loading ? 'Loading...' : $settings.customizer.customer_information.login_button }}</Button>
        </template>
    </Main>
</template>